import type { WeekDayState, WeekDaySummary } from './todayData.js'

const TONE: Record<WeekDayState, string> = {
  short: 'bg-coral-lt text-coral border-coral/30',
  open: 'bg-gold-lt text-ink border-gold/40',
  ok: 'bg-green-lt text-green border-green/30',
  unknown: 'bg-canvas text-ink-mid border-transparent',
}

function stateText(day: WeekDaySummary): string {
  if (day.state === 'short') {
    return `${day.short} short`
  }
  if (day.state === 'open') {
    return `${day.open} open`
  }
  return day.state === 'ok' ? 'ok' : '—'
}

function weekday(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
}

/**
 * The week ahead on the Today tab (facility operations dashboard plan §3, mockup screen 1): a day a cell with its
 * census (recorded bold, forecast grey italics) and its coverage - coral where seats are short with nobody offered
 * them, gold where every gap is on the board being offered, green when covered. A day redraws The day at a glance for
 * it (since 27 October 2026, when the Coverage today grid was folded into the heatmap).
 */
export function WeekAhead({ days, selected, onSelect, released }: { days: readonly WeekDaySummary[]; selected: string; onSelect: (date: string) => void; released: boolean }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">The week ahead · census and coverage</div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => (
          <button
            key={day.date}
            type="button"
            aria-pressed={day.date === selected}
            aria-label={`${weekday(day.date)} ${day.date}: census ${day.census ?? 'none'}, ${stateText(day)}`}
            onClick={() => onSelect(day.date)}
            className={`rounded border px-1 py-1.5 text-center text-[10px] font-bold leading-tight ${TONE[day.state]} ${day.date === selected ? 'ring-2 ring-marine' : 'hover:ring-1 hover:ring-marine/40'}`}
          >
            <span className="block">{weekday(day.date)}</span>
            <span className={`block text-sm ${day.recorded ? 'font-black' : 'font-bold italic opacity-75'}`}>{day.census ?? '—'}</span>
            <span className="block">{stateText(day)}</span>
          </button>
        ))}
      </div>
      <p className="mt-1 text-[10px] text-ink-mid">
        {released ? 'Grey italics are forecasts. The outlined day is the one The day at a glance draws; pick another to redraw it.' : 'Grey italics are forecasts. Coverage is coloured once the month is released. The outlined day is the one The day at a glance draws.'}
      </p>
    </div>
  )
}
